import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorderOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import ContactSupportOutlinedIcon from '@mui/icons-material/ContactSupportOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNoneOutlined';
import TollIcon from '@mui/icons-material/Toll';
import Tab from '@mui/material/Tab';
import { Link, useRouterState } from '@tanstack/react-router';
import { useMemo, type ComponentType, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { ScrollableTabs } from '../../../components/ScrollableTabs';
import { selectActiveMembership, selectIsPendingValidation, useCurrentUser } from '../../../lib/auth';
import { settingsTabPath } from '../routes';
import { PENDING_VALIDATION_SETTINGS_TAB_IDS, SETTINGS_TAB_IDS, type SettingsTabId } from '../types';

const TAB_ICONS: Record<SettingsTabId, ComponentType> = {
    company: BusinessOutlinedIcon,
    verification: FactCheckOutlinedIcon,
    account: AccountCircleOutlinedIcon,
    members: GroupsOutlinedIcon,
    notifications: NotificationsNoneIcon,
    subscription: BookmarkBorderIcon,
    credits: TollIcon,
    support: ContactSupportOutlinedIcon,
};

export function SettingsTabs(): ReactElement {
    const { t } = useTranslation();
    const pathname = useRouterState({ select: (s) => s.location.pathname });

    const currentUser = useCurrentUser();
    const isPendingValidation = selectIsPendingValidation(currentUser);
    const usesCredits = selectActiveMembership(currentUser)?.usesCredits === true;
    const visibleTabIds = useMemo<readonly SettingsTabId[]>(() => {
        if (isPendingValidation) {
            return PENDING_VALIDATION_SETTINGS_TAB_IDS;
        }
        const isVisible: Partial<Record<SettingsTabId, boolean>> = {
            credits: usesCredits,
        };
        return SETTINGS_TAB_IDS.filter((id) => isVisible[id] ?? true);
    }, [isPendingValidation, usesCredits]);

    const activeTab = useMemo<SettingsTabId>(() => {
        const match = visibleTabIds.find((id) => pathname.startsWith(settingsTabPath(id)));
        return match ?? 'company';
    }, [pathname, visibleTabIds]);

    return (
        <ScrollableTabs value={activeTab} aria-label={t('SETTINGS.TABS.ARIA_LABEL')}>
            {visibleTabIds.map((id) => {
                const Icon = TAB_ICONS[id];
                return (
                    <Tab
                        key={id}
                        value={id}
                        label={t(`SETTINGS.TABS.${id.toUpperCase()}`)}
                        icon={<Icon />}
                        iconPosition="start"
                        component={Link}
                        to={settingsTabPath(id)}
                        sx={{ gap: 1 }}
                    />
                );
            })}
        </ScrollableTabs>
    );
}
